import { Tab as HeadlessTab } from "@headlessui/react";
import { motion } from "framer-motion";
import React from "react";
import { Link } from "react-router-dom";

import { useUrlTabLinkTo } from "../hooks/useUrlTabLinkTo";
import { styles } from "../styles";
import { TabItem as TabItemType } from "../types";

import { DynamicTab } from "./DynamicTab";
import { SubTabItem } from "./SubTabItem";
import { TabContent } from "./TabContent";

interface TabProps {
  tab: TabItemType;
  isActive: boolean;
  vertical?: boolean;
  handleTabChange?: (index: number) => void;
  getTabLinkPath?: (tab: TabItemType) => string;
  isSubTabActive?: (subTab: TabItemType) => boolean;
  isRightAligned?: boolean;
  index?: number;
  currentIndex?: number;
  isDragging?: boolean;
  isDynamic?: boolean;
  staticTabsCount?: number;
}

export const Tab = ({
  tab,
  isActive,
  vertical = false,
  handleTabChange,
  getTabLinkPath,
  isSubTabActive,
  isRightAligned = false,
  index,
  currentIndex,
  isDragging = false,
  isDynamic = false,
  staticTabsCount = 0,
}: TabProps) => {
  const actualIsActive =
    isDynamic && currentIndex !== undefined && index !== undefined
      ? currentIndex === index + staticTabsCount
      : isActive;

  const getTabClassName = () => {
    if (isDynamic) {
      return `relative flex h-10 cursor-grab items-center border-b-2 px-4 py-2.5 text-sm font-medium outline-none transition-colors duration-300 ease-in-out
                ${isDragging ? "bg-gray-100 opacity-80 shadow-md" : ""}
                ${
                  actualIsActive
                    ? "border-blue-600 text-blue-600 after:absolute after:bottom-0 after:left-0 after:h-[2px] after:w-full after:translate-y-[2px] after:bg-blue-600/20 after:blur-sm"
                    : "border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-200"
                } ${tab.disabled ? "cursor-not-allowed opacity-50" : ""} ${tab.className || ""}`;
    }

    const borderClass = vertical ? styles.tab.border.vertical : styles.tab.border.horizontal;
    const activeClass = actualIsActive
      ? `${styles.tab.active.base} ${
          vertical ? styles.tab.active.vertical : styles.tab.active.horizontal
        }`
      : styles.tab.inactive;
    const disabledClass = tab.disabled ? "cursor-not-allowed opacity-50 pointer-events-none" : "";

    return `${styles.tab.base} ${borderClass} ${activeClass} ${disabledClass} ${
      tab.className || ""
    }`;
  };

  const tabProps = {
    onClick: (e: React.MouseEvent) => {
      if (tab.disabled) {
        e.preventDefault();
        return;
      }
      handleTabChange?.(tab.id);
      if (!tab.path) {
        e.preventDefault();
      }
      tab.onClick?.(e);
    },
    className: getTabClassName(),
    "aria-label": `${tab.name} tab`,
    disabled: tab.disabled,
  };

  const getTabLinkPathOrDefault = (tabItem: TabItemType) => {
    if (getTabLinkPath) {
      return getTabLinkPath(tabItem);
    }
    if (tabItem.subTabs && tabItem.subTabs.length > 0) {
      return `${tabItem.path}/${tabItem.subTabs[0].path}`;
    }
    return tabItem.path || "#";
  };

  const tabLinkPathname = tab.path ? getTabLinkPathOrDefault(tab) : null;
  const urlTabLinkTo = useUrlTabLinkTo(tabLinkPathname);

  return (
    <div className={`relative ${vertical ? "!ml-0" : ""}`}>
      {tab.path ? (
        <HeadlessTab as={Link} to={urlTabLinkTo ?? "#"} {...tabProps}>
          {isDynamic ? (
            <DynamicTab tab={tab} actualIsActive={actualIsActive} />
          ) : (
            <TabContent
              solidIcon={tab.solidIcon}
              outlineIcon={tab.outlineIcon}
              name={tab.name}
              isActive={actualIsActive}
              isRightAligned={isRightAligned}
              count={tab.count}
            />
          )}
        </HeadlessTab>
      ) : (
        <HeadlessTab {...tabProps}>
          {isDynamic ? (
            <DynamicTab tab={tab} actualIsActive={actualIsActive} />
          ) : (
            <TabContent
              solidIcon={tab.solidIcon}
              outlineIcon={tab.outlineIcon}
              name={tab.name}
              isActive={actualIsActive}
              isRightAligned={isRightAligned}
              count={tab.count}
            />
          )}
        </HeadlessTab>
      )}

      {!isDynamic && tab.subTabs && isSubTabActive && (
        <motion.div
          className="relative left-[20px] top-0 flex w-[calc(100%-20px)] flex-col"
          initial={{ opacity: 0, height: "0px" }}
          animate={{
            opacity: 1,
            height: actualIsActive ? "auto" : "0px",
          }}
          exit={{ opacity: 0, height: "0px" }}
          style={{ overflow: "hidden" }}
          transition={{ duration: 0.2 }}
        >
          {tab.subTabs.map(subTab => (
            <SubTabItem
              key={subTab.id}
              subTab={subTab}
              parentTab={tab}
              isSubActive={isSubTabActive(subTab)}
              vertical={vertical}
              handleTabChange={handleTabChange}
            />
          ))}
        </motion.div>
      )}
    </div>
  );
};
